import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/logo'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { fetchAvailableProducts, sendProductRequest } from '@/services/products'
import type { AvailableProduct } from '@/types/products'
import {
  AlertCircle,
  Package,
  Search,
  X,
  Hash,
  ShoppingCart,
  CheckCircle2,
  Plus,
} from 'lucide-react'

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function AvailableProducts() {
  const { t, i18n } = useTranslation()
  const [products, setProducts] = useState<AvailableProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCell, setFilterCell] = useState<string>('all')

  // Request sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] =
    useState<AvailableProduct | null>(null)
  const [quantity, setQuantity] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Custom request sheet state
  const [isCustomSheetOpen, setIsCustomSheetOpen] = useState(false)
  const [customThickness, setCustomThickness] = useState('')
  const [customCell, setCustomCell] = useState('')
  const [customHeight, setCustomHeight] = useState('')
  const [customWidth, setCustomWidth] = useState('')
  const [customQuantity, setCustomQuantity] = useState('')
  const [customPhoneNumber, setCustomPhoneNumber] = useState('')
  const [isCustomSubmitting, setIsCustomSubmitting] = useState(false)
  const [customSubmitSuccess, setCustomSubmitSuccess] = useState(false)
  const [customSubmitError, setCustomSubmitError] = useState<string | null>(
    null
  )

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchAvailableProducts()
        const sorted = [...data].sort((a, b) => {
          if (b.cell !== a.cell) return b.cell - a.cell
          return a.thickness - b.thickness
        })
        setProducts(sorted)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products')
      } finally {
        setIsLoading(false)
      }
    }

    loadProducts()
  }, [])

  // Get unique cell sizes for filter
  const uniqueCells = useMemo(() => {
    const cells = new Set<number>()
    products.forEach((p) => cells.add(p.cell))
    return Array.from(cells).sort((a, b) => a - b)
  }, [products])

  const filteredProducts = useMemo(() => {
    let result = products.filter((item) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        item.thickness.toString().includes(searchLower) ||
        item.cell.toString().includes(searchLower) ||
        item.height.toString().includes(searchLower) ||
        item.width.toString().includes(searchLower)

      const matchesCell =
        filterCell === 'all' || item.cell.toString() === filterCell

      return matchesSearch && matchesCell
    })

    return result
  }, [products, searchQuery, filterCell])

  const clearFilters = () => {
    setSearchQuery('')
    setFilterCell('all')
  }

  const hasActiveFilters = searchQuery !== '' || filterCell !== 'all'

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ'

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(num)
  }

  const totalProducts = products.length
  const totalCount = products.reduce((sum, p) => sum + p.total_count, 0)

  const handleOpenRequest = (product: AvailableProduct) => {
    setSelectedProduct(product)
    setQuantity('')
    setPhoneNumber('')
    setSubmitSuccess(false)
    setSubmitError(null)
    setIsSheetOpen(true)
  }

  const handleSubmitRequest = async () => {
    if (!selectedProduct || !quantity || !phoneNumber) return

    const qty = parseInt(quantity, 10)
    if (isNaN(qty) || qty <= 0) {
      setSubmitError(t('availableProducts.invalidQuantity'))
      return
    }

    if (qty > selectedProduct.total_count) {
      setSubmitError(t('availableProducts.quantityExceedsStock'))
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await sendProductRequest({
        thickness: selectedProduct.thickness,
        cell: selectedProduct.cell,
        height: selectedProduct.height,
        width: selectedProduct.width,
        quantity: qty,
        phone_number: phoneNumber,
      })
      setSubmitSuccess(true)
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t('availableProducts.requestFailed')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseSheet = () => {
    setIsSheetOpen(false)
    setSelectedProduct(null)
    setQuantity('')
    setPhoneNumber('')
    setSubmitSuccess(false)
    setSubmitError(null)
  }

  const handleOpenCustomRequest = () => {
    setCustomThickness('')
    setCustomCell('')
    setCustomHeight('')
    setCustomWidth('')
    setCustomQuantity('')
    setCustomPhoneNumber('')
    setCustomSubmitSuccess(false)
    setCustomSubmitError(null)
    setIsCustomSheetOpen(true)
  }

  const handleSubmitCustomRequest = async () => {
    if (
      !customThickness ||
      !customCell ||
      !customHeight ||
      !customWidth ||
      !customQuantity ||
      !customPhoneNumber
    ) {
      setCustomSubmitError(t('availableProducts.allFieldsRequired'))
      return
    }

    const thickness = parseFloat(customThickness)
    const cell = parseFloat(customCell)
    const height = parseFloat(customHeight)
    const width = parseFloat(customWidth)
    const qty = parseInt(customQuantity, 10)

    if (
      isNaN(thickness) ||
      isNaN(cell) ||
      isNaN(height) ||
      isNaN(width) ||
      isNaN(qty) ||
      thickness <= 0 ||
      cell <= 0 ||
      height <= 0 ||
      width <= 0 ||
      qty <= 0
    ) {
      setCustomSubmitError(t('availableProducts.invalidValues'))
      return
    }

    setIsCustomSubmitting(true)
    setCustomSubmitError(null)

    try {
      await sendProductRequest({
        thickness,
        cell,
        height,
        width,
        quantity: qty,
        phone_number: customPhoneNumber,
      })
      setCustomSubmitSuccess(true)
    } catch (err) {
      setCustomSubmitError(
        err instanceof Error
          ? err.message
          : t('availableProducts.requestFailed')
      )
    } finally {
      setIsCustomSubmitting(false)
    }
  }

  const handleCloseCustomSheet = () => {
    setIsCustomSheetOpen(false)
    setCustomThickness('')
    setCustomCell('')
    setCustomHeight('')
    setCustomWidth('')
    setCustomQuantity('')
    setCustomPhoneNumber('')
    setCustomSubmitSuccess(false)
    setCustomSubmitError(null)
  }

  return (
    <div className='min-h-svh bg-background'>
      {/* Header */}
      <header className='border-b'>
        <div className='container mx-auto flex h-16 items-center justify-between px-4'>
          <Link to='/' className='flex items-center'>
            <Logo className='h-8 w-auto text-sidebar-primary' />
          </Link>
          <div className='flex items-center gap-2'>
            <Select
              value={i18n.language}
              onValueChange={(value) => i18n.changeLanguage(value)}
            >
              <SelectTrigger className='w-32'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='uz'>{t('language.uz')}</SelectItem>
                <SelectItem value='ru'>{t('language.ru')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='container mx-auto px-2 lg:px-4 py-8'>
        {/* Page Title */}
        <div className='mb-8'>
          <h1 className='text-3xl font-bold tracking-tight'>
            {t('availableProducts.title')}
          </h1>
          <p className='text-muted-foreground mt-2'>
            {t('availableProducts.description')}
          </p>
        </div>

        {error && (
          <div className='mb-6 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
            <AlertCircle className='h-4 w-4' />
            <span>{error}</span>
          </div>
        )}

        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-2 mb-8'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('availableProducts.productTypes')}
              </CardTitle>
              <Package className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {isLoading ? <Skeleton className='h-8 w-16' /> : totalProducts}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('availableProducts.totalCount')}
              </CardTitle>
              <Hash className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {isLoading ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  formatNumber(totalCount)
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Table Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('availableProducts.inventoryTitle')}</CardTitle>
            <CardDescription>
              {t('availableProducts.inventoryDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='px-2 lg:px-6'>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-50'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('availableProducts.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Select value={filterCell} onValueChange={setFilterCell}>
                <SelectTrigger className='w-40'>
                  <SelectValue placeholder={t('availableProducts.cell')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('availableProducts.allCells')}
                  </SelectItem>
                  {uniqueCells.map((cell) => (
                    <SelectItem key={cell} value={cell.toString()}>
                      {cell}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredProducts.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {products.length === 0
                  ? t('availableProducts.noProductsFound')
                  : t('availableProducts.noProductsMatch')}
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='text-right px-1'>
                        {t('availableProducts.cell')}
                      </TableHead>
                      <TableHead className='text-right px-1'>
                        {t('availableProducts.thickness')}
                      </TableHead>
                      <TableHead className='text-right px-1'>
                        {t('availableProducts.height')}
                      </TableHead>
                      <TableHead className='text-right px-1'>
                        {t('availableProducts.width')}
                      </TableHead>
                      <TableHead className='text-right px-1'>
                        {t('availableProducts.count')}
                      </TableHead>
                      <TableHead className='text-center'>
                        {/* {t('common.actions')} */}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map((item, index) => (
                      <TableRow
                        key={`${item.thickness}-${item.cell}-${item.height}-${item.width}-${index}`}
                      >
                        <TableCell className='text-right'>
                          {formatNumber(item.cell)}
                        </TableCell>
                        <TableCell className='text-right'>
                          {/* <span className='rounded-full bg-primary/10 text-primary px-2 py-1 text-xs font-medium'> */}
                          {formatNumber(item.thickness)}
                          {/* </span> */}
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatNumber(item.height)} m
                        </TableCell>
                        <TableCell className='text-right'>
                          {formatNumber(item.width)} m
                        </TableCell>
                        <TableCell className='text-right font-medium'>
                          {formatNumber(item.total_count)}
                        </TableCell>
                        <TableCell className='text-center p-1'>
                          <Button
                            size='icon-sm'
                            onClick={() => handleOpenRequest(item)}
                          >
                            <ShoppingCart className='mr-1 h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Results count */}
            {!isLoading && products.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                {t('common.showing')} {filteredProducts.length} {t('common.of')}{' '}
                {products.length}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Request Section */}
        <Card className='mt-8'>
          <CardHeader className='text-center'>
            <CardTitle>{t('availableProducts.customRequest')}</CardTitle>
            <CardDescription>
              {t('availableProducts.customRequestDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex justify-center'>
            <Button size='lg' onClick={handleOpenCustomRequest}>
              <Plus className='mr-2 h-5 w-5' />
              {t('availableProducts.customRequestButton')}
            </Button>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <div className='mt-8 text-center text-muted-foreground'>
          <p>{t('availableProducts.contactInfo')}</p>
        </div>
      </main>

      {/* Request Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={handleCloseSheet}>
        <SheetContent className='px-4 overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>{t('availableProducts.requestTitle')}</SheetTitle>
            <SheetDescription>
              {t('availableProducts.requestDescription')}
            </SheetDescription>
          </SheetHeader>

          {submitSuccess ? (
            <div className='flex flex-col items-center justify-center py-8 gap-4'>
              <div className='rounded-full bg-green-100 p-3 dark:bg-green-900'>
                <CheckCircle2 className='h-8 w-8 text-green-600 dark:text-green-400' />
              </div>
              <h3 className='text-lg font-semibold'>
                {t('availableProducts.requestSuccess')}
              </h3>
              <p className='text-sm text-muted-foreground text-center'>
                {t('availableProducts.requestSuccessMessage')}
              </p>
              <Button onClick={handleCloseSheet} className='mt-4'>
                {t('common.close')}
              </Button>
            </div>
          ) : (
            <>
              {selectedProduct && (
                <div className='space-y-6 py-6'>
                  {/* Product Info */}
                  <div className='rounded-lg border bg-muted/50 p-4'>
                    <h4 className='text-sm font-medium mb-3'>
                      {t('availableProducts.productInfo')}
                    </h4>
                    <div className='grid grid-cols-2 gap-2 text-sm'>
                      <div className='text-muted-foreground'>
                        {t('availableProducts.thickness')}:
                      </div>
                      <div className='font-medium'>
                        {formatNumber(selectedProduct.thickness)} mm
                      </div>
                      <div className='text-muted-foreground'>
                        {t('availableProducts.cell')}:
                      </div>
                      <div className='font-medium'>
                        {formatNumber(selectedProduct.cell)}
                      </div>
                      <div className='text-muted-foreground'>
                        {t('availableProducts.height')}:
                      </div>
                      <div className='font-medium'>
                        {formatNumber(selectedProduct.height)} m
                      </div>
                      <div className='text-muted-foreground'>
                        {t('availableProducts.width')}:
                      </div>
                      <div className='font-medium'>
                        {formatNumber(selectedProduct.width)} m
                      </div>
                      <div className='text-muted-foreground'>
                        {t('availableProducts.available')}:
                      </div>
                      <div className='font-medium'>
                        {formatNumber(selectedProduct.total_count)}
                      </div>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='quantity'>
                        {t('availableProducts.quantity')}
                      </Label>
                      <Input
                        id='quantity'
                        type='number'
                        min='1'
                        max={selectedProduct.total_count}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder={t('availableProducts.quantityPlaceholder')}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='phone'>
                        {t('availableProducts.phoneNumber')}
                      </Label>
                      <Input
                        id='phone'
                        type='tel'
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder='+998 90 123 45 67'
                      />
                    </div>
                  </div>

                  {submitError && (
                    <div className='flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
                      <AlertCircle className='h-4 w-4 flex-shrink-0' />
                      <span>{submitError}</span>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={handleSubmitRequest}
                disabled={isSubmitting || !quantity || !phoneNumber}
                className='w-full'
              >
                {isSubmitting
                  ? t('common.loading')
                  : t('availableProducts.sendRequest')}
              </Button>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Custom Request Sheet */}
      <Sheet open={isCustomSheetOpen} onOpenChange={handleCloseCustomSheet}>
        <SheetContent className='px-4 overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>{t('availableProducts.customRequestTitle')}</SheetTitle>
            <SheetDescription>
              {t('availableProducts.customRequestDescription')}
            </SheetDescription>
          </SheetHeader>

          {customSubmitSuccess ? (
            <div className='flex flex-col items-center justify-center py-8 gap-4'>
              <div className='rounded-full bg-green-100 p-3 dark:bg-green-900'>
                <CheckCircle2 className='h-8 w-8 text-green-600 dark:text-green-400' />
              </div>
              <h3 className='text-lg font-semibold'>
                {t('availableProducts.requestSuccess')}
              </h3>
              <p className='text-sm text-muted-foreground text-center'>
                {t('availableProducts.requestSuccessMessage')}
              </p>
              <Button onClick={handleCloseCustomSheet} className='mt-4'>
                {t('common.close')}
              </Button>
            </div>
          ) : (
            <>
              <div className='space-y-4 py-6'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='customThickness'>
                      {t('availableProducts.thickness')} (mm)
                    </Label>
                    <Input
                      id='customThickness'
                      type='number'
                      step='0.1'
                      min='0'
                      value={customThickness}
                      onChange={(e) => setCustomThickness(e.target.value)}
                      placeholder={t('availableProducts.enterThickness')}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='customCell'>
                      {t('availableProducts.cell')}
                    </Label>
                    <Input
                      id='customCell'
                      type='number'
                      min='0'
                      value={customCell}
                      onChange={(e) => setCustomCell(e.target.value)}
                      placeholder={t('availableProducts.enterCell')}
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='customHeight'>
                      {t('availableProducts.height')} (m)
                    </Label>
                    <Input
                      id='customHeight'
                      type='number'
                      step='0.1'
                      min='0'
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      placeholder={t('availableProducts.enterHeight')}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='customWidth'>
                      {t('availableProducts.width')} (m)
                    </Label>
                    <Input
                      id='customWidth'
                      type='number'
                      step='0.1'
                      min='0'
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      placeholder={t('availableProducts.enterWidth')}
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='customQuantity'>
                    {t('availableProducts.quantity')}
                  </Label>
                  <Input
                    id='customQuantity'
                    type='number'
                    min='1'
                    value={customQuantity}
                    onChange={(e) => setCustomQuantity(e.target.value)}
                    placeholder={t('availableProducts.enterQuantity')}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='customPhone'>
                    {t('availableProducts.phoneNumber')}
                  </Label>
                  <Input
                    id='customPhone'
                    type='tel'
                    value={customPhoneNumber}
                    onChange={(e) => setCustomPhoneNumber(e.target.value)}
                    placeholder='+998 90 123 45 67'
                  />
                </div>

                {customSubmitError && (
                  <div className='flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
                    <AlertCircle className='h-4 w-4 flex-shrink-0' />
                    <span>{customSubmitError}</span>
                  </div>
                )}
              </div>

              <Button
                onClick={handleSubmitCustomRequest}
                disabled={
                  isCustomSubmitting ||
                  !customThickness ||
                  !customCell ||
                  !customHeight ||
                  !customWidth ||
                  !customQuantity ||
                  !customPhoneNumber
                }
                className='w-full'
              >
                {isCustomSubmitting
                  ? t('common.loading')
                  : t('availableProducts.sendRequest')}
              </Button>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
